import BrandingError from "%/dynamic-component-editor/errors/branding-error";
import { mapBrandingConfigToElements } from "%/dynamic-component-editor/classes/branding/branding-helper";
import App from "@bloglovin/inspire-engine/App";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import { isCompiledRenderSurface } from "%/dynamic-component-editor/classes/platform-standard-entities";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useConfigurationStore = (...args) => require("%/dynamic-component-editor/stores/configuration-store").useConfigurationStore(...args);
// eslint-disable-next-line global-require
const useBrandingStore = (...args) => require("%/dynamic-component-editor/stores/branding-store").default(...args);

export default class BaseBranding {
  /** ******************* Static Vars *********************/
  static inheritanceStructure = {};
  static brandingSpecificOptionKeys = [
    "button-type",
    "button-size",
    "button-variant",
    "styled-text-option",
    "styled-text-variant",
    "text-branding-style-option",
  ];

  /** ******************* Static Getters *********************/
  static get baseVariants() {
    return [
      "standard.standard",
      "primary.standard",
    ];
  }

  // Inspector-only base coordinates. The Inspector treats these as the
  // editable root for their variant chain — no "inheriting from" badge,
  // no "this overrides X" badge, no inheritance affordance — even when
  // the rendering inheritance graph (`baseVariants` + each provider's
  // `inheritanceStructure.inherits`) still walks past them. Defaults to
  // `baseVariants` so providers that don't opt in keep the legacy
  // behaviour. Override on a provider when a sub-variant root (e.g.
  // ButtonBranding's `secondary.standard`) is conceptually self-contained
  // for editing even though it inherits from `primary.standard` at render
  // time.
  static get inspectorBaseVariants() {
    return this.baseVariants;
  }

  // Every concrete branding provider the Studio Branding section
  // and the resolver chain know about. Order is the sidebar's
  // display order. Resolved lazily through `require` so the
  // subclass modules — which import this file — don't hit a
  // circular-import edge at evaluation time; each call returns a
  // fresh array so consumers can `.map(...)` without mutating the
  // shared registry.
  static get allProviders() {
    /* eslint-disable global-require */
    const TypographyBranding = require("%/dynamic-component-editor/classes/branding/typography-branding").default;
    const ButtonBranding = require("%/dynamic-component-editor/classes/branding/button-branding").default;
    /* eslint-enable global-require */
    return [TypographyBranding, ButtonBranding];
  }

  // Look a provider up by its `categoryKey`. Used by the Branding
  // section's primary content to resolve which provider drives the
  // preview grid for the URL segment the user navigated to.
  static getProviderByCategoryKey(categoryKey) {
    return this.allProviders.find(provider => provider.categoryKey === categoryKey) ?? null;
  }

  // Look a provider up by an element's `type` field. Used by the
  // BrandingElementPreview to decide whether a given seeded
  // BrandingElement should render as iText (typography) or
  // iButton (button).
  static getProviderByElementType(elementType) {
    return this.allProviders.find(provider => provider.ownsElementType?.(elementType)) ?? null;
  }

  // Capitalise the first letter of a variant or size token for
  // display. Used by every provider's `getPreviewSpecs()` —
  // factored up so subclasses don't redefine it.
  static titleCase(value) {
    if (!value) {
      return "";
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  // Preview cards grouped into sections for the Branding editor. Default
  // is one section (all specs); providers may override to split (see Typography).
  static getPreviewGroups() {
    return [
      {
        heading: this.categoryTitle,
        previews: this.getPreviewSpecs(),
      },
    ];
  }

  // BrandingElements persist with EMPTY option trees. The row exists
  // only as a coordinate marker for `(elementType, brandedVariant,
  // brandedSize)` — every actual value the user sees is computed at
  // read time by the resolver:
  //
  //   1. Override stored on this row's options tree wins.
  //   2. Otherwise the resolver walks the inheritance chain via
  //      `findNextInheritanceLevel`, looking for an override at
  //      each parent coord (`Heading/Accent → Heading/Standard`;
  //      `Button/Secondary/Small → Button/Secondary/Standard →
  //      Button/Primary/Standard`).
  //   3. If no level in the chain has an override, the legacy
  //      branding-store config (`useBrandingStore().configuration`)
  //      supplies the value via `BaseBranding.getRawValue`.
  //   4. Final fallback is the OptionClass static default.
  //
  // Storing template values in the DB at seed time was the wrong
  // shape — it baked the inheritance defaults into every row, so a
  // later edit on Heading/Standard didn't reach Heading/Accent (its
  // seed snapshot stayed stale) and the destination's button rows
  // shipped 0px padding for every non-base coord (the template only
  // populates `primary.standard`). Empty seeds + read-time chain
  // walk is one consistent model across both branding providers.
  // eslint-disable-next-line no-unused-vars
  static buildSeedOptionsForVariantSize(elementType, brandedVariant, brandedSize) {
    return JSON.stringify({
      "light-mode": { default: { default: {} } },
      "dark-mode": { default: { default: {} } },
    });
  }

  // Pretty-printed display name written onto each lazily created
  // BrandingElement (e.g. "Heading / Standard", "Button / Primary
  // / Standard"). Subclasses can override; the base form covers the
  // common case where the label is `<typeLabel> / <Variant>` and
  // optionally `/ <Size>`.
  static buildSeedDisplayName(elementType, brandedVariant, brandedSize) {
    const typeEntry = (this.elementTypes ?? []).find(entry => entry.type === elementType);
    const typeLabel = typeEntry?.label ?? elementType;
    const variantLabel = this.titleCase(brandedVariant);
    if (!brandedSize) {
      return `${typeLabel} / ${variantLabel}`;
    }
    return `${typeLabel} / ${variantLabel} / ${this.titleCase(brandedSize)}`;
  }

  // Same-tab dedupe set: tracks which (destination, type, variant,
  // size) tuples we've already dispatched an AddThemeElement event
  // for in this session. The schema's unique index is the cross-tab
  // safety net; this avoids spamming the App engine with duplicate
  // events while the first one is still in flight.
  static #lazyCreateRequested = new Set();

  // Per-destination index built from the `themeElements` feed —
  // `(type|variant|size) → BrandingElement`. Rebuilt only when the
  // feed mutates (an item is added or removed), keyed by the
  // feed's joined-id snapshot. Without this cache canvas drops
  // pay an O(N) feed walk per option per inheritance hop, which
  // adds up fast on a heavy element class.
  static #themeElementIndexByDestination = new Map();
  static #themeElementIndexRevByDestination = new Map();

  static #themeElementKey(elementType, brandedVariant, brandedSize) {
    return `${elementType}|${brandedVariant ?? ""}|${brandedSize ?? ""}`;
  }

  // True once the destination's `themeElements` feed has integrated
  // at least one server response — the gate `findOrCreateThemeElementFor`
  // uses to avoid firing AddThemeElement before we know whether the
  // server already has the row. Without this gate the typography
  // page racing against a still-pending feed load fires creates for
  // all 27 (type, variant, size) tuples; if any of them already
  // exist, the unique index on the element table 500s the request.
  static isThemeElementsFeedLoaded(destinationId) {
    // Branding is frozen into the compiled defaults on render surfaces — no live themeElements feed.
    if (isCompiledRenderSurface()) {
      return false;
    }
    if (destinationId === null || destinationId === undefined || destinationId === "") {
      return false;
    }
    return Boolean(App.get("CreatorStudio").getFeedMeta(
      "Destination",
      { id: String(destinationId) },
      "themeElements",
      "hasLoadedAtLeastOnce",
    ));
  }

  // Walk the destination's `themeElements` feed for the row that
  // matches this (type, variant, size) coordinate. Returns the
  // BrandingElement instance or null when no row exists yet —
  // callers that need lazy creation should reach for
  // `findOrCreateThemeElementFor` instead, which fires
  // AddThemeElement on miss.
  //
  // The lookup is cached per destination so canvas-time consumers
  // (`DynamicElement.buildBrandingDefaultLeaf` walks the
  // inheritance chain for every themable option per element) hit
  // an O(1) Map lookup instead of an O(N) feed walk.
  static findThemeElementFor(destinationId, elementType, brandedVariant, brandedSize) {
    // Render surfaces carry branding frozen into the compiled defaults; there is no live feed to walk.
    if (isCompiledRenderSurface()) {
      return null;
    }
    if (destinationId === null || destinationId === undefined || destinationId === "") {
      return null;
    }
    const destinationKey = String(destinationId);
    const feedItems = App.get("CreatorStudio")
        .getFeedItems("Destination", { id: destinationKey }, "themeElements") ?? [];
    const revision = `${feedItems.length}|${feedItems.map(item => item?.entityPk?.id ?? "").join(",")}`;

    let index = this.#themeElementIndexByDestination.get(destinationKey);
    if (this.#themeElementIndexRevByDestination.get(destinationKey) !== revision) {
      index = new Map();
      for (const item of feedItems) {
        const elementId = item?.entityPk?.id;
        if (!elementId) {
          continue;
        }
        const element = DynamicElement.get(String(elementId));
        // Check the underlying entity's `source` field rather than the
        // cached subclass's `isTheme` — DynamicElement.get() picks
        // DynamicElement vs BrandingElement based on the entity state at
        // first call. If the feed delivered the entity row in the same
        // sync as the feed entry, the instance may have been created
        // before `source` hydrated, locking in the wrong subclass and
        // returning false from `isTheme` forever. Reading the live entity
        // here avoids that race and prevents duplicate-key 500s when
        // `findOrCreateThemeElementFor` retries the create for a row the
        // server already has.
        const isTheme = element?.element?.source === "theme";
        if (!isTheme) {
          continue;
        }
        // Read coordinate fields off the entity directly. The
        // `brandedVariant` / `brandedSize` getters only exist on
        // `BrandingElement`, so a misclassified `DynamicElement`
        // instance returns undefined for both and the key collapses
        // to `${type}|undefined|undefined` — which never matches a
        // canvas lookup, so a row that exists on the server stays
        // unfindable and the create fires again.
        const elementType = element.element?.type;
        const brandedVariant = element.element?.brandedVariant;
        const brandedSize = element.element?.brandedSize;
        const key = this.#themeElementKey(elementType, brandedVariant, brandedSize);
        // Prefer real (server-confirmed) entities over temp-PK
        // duplicates. Earlier sync regressions left orphan temp
        // entities in the JS feed at the same coordinate as the
        // real row — last-write-wins handed the empty temp to the
        // preview and ate the user's overrides. Numeric PKs from
        // the server are auto-increment small ints; JS temp PKs are
        // 13-digit timestamps, so any existing entity with a
        // smaller-numbered PK at the same key wins.
        const existing = index.get(key);
        if (existing) {
          const existingId = Number(existing.id);
          const candidateId = Number(element.id);
          if (Number.isFinite(existingId) && Number.isFinite(candidateId) && existingId < candidateId) {
            continue;
          }
        }
        index.set(key, element);
      }
      this.#themeElementIndexByDestination.set(destinationKey, index);
      this.#themeElementIndexRevByDestination.set(destinationKey, revision);
    }
    return index.get(this.#themeElementKey(elementType, brandedVariant, brandedSize)) ?? null;
  }

  // Look up the BrandingElement for this coordinate, and if it
  // doesn't exist yet, fire `AddThemeElement` so the App engine
  // creates it (immediately on the JS side, then synced to the
  // server). Returns the existing element when the feed already
  // has it; returns null while a fresh create is in flight —
  // callers that need a real entity should retry on the reactive
  // feed update.
  static findOrCreateThemeElementFor(destinationId, elementType, brandedVariant, brandedSize) {
    const existing = this.findThemeElementFor(destinationId, elementType, brandedVariant, brandedSize);
    if (existing) {
      return existing;
    }
    // Wait for the destination's themeElements feed to settle before
    // optimistically creating — otherwise we race against rows the
    // server may already have, and the unique index 500s the create.
    // Once the feed integrates a response Vue reactivity will re-run
    // this caller and we'll either find the existing row or create a
    // genuinely missing one.
    if (!this.isThemeElementsFeedLoaded(destinationId)) {
      return null;
    }
    this.#requestThemeElementCreate(destinationId, elementType, brandedVariant ?? "", brandedSize ?? "");
    return null;
  }

  static #requestThemeElementCreate(destinationId, elementType, brandedVariant, brandedSize) {
    if (destinationId === null || destinationId === undefined || destinationId === "") {
      return;
    }
    const dedupeKey = `${destinationId}|${elementType}|${brandedVariant}|${brandedSize}`;
    if (this.#lazyCreateRequested.has(dedupeKey)) {
      return;
    }
    const provider = this.getProviderByElementType(elementType);
    if (!provider) {
      return;
    }
    this.#lazyCreateRequested.add(dedupeKey);
    try {
      App.get("CreatorStudio").triggerEvent("AddThemeElement", {
        destination: { id: String(destinationId) },
        elementType,
        brandedVariant,
        brandedSize,
        options: provider.buildSeedOptionsForVariantSize(elementType, brandedVariant, brandedSize),
        displayName: provider.buildSeedDisplayName(elementType, brandedVariant, brandedSize),
      });
    } catch (err) {
      // Drop the dedupe key so a future read retries — the create
      // failed before the App engine recorded it.
      this.#lazyCreateRequested.delete(dedupeKey);
      // eslint-disable-next-line no-console
      console.error("[branding] AddThemeElement failed", { destinationId, elementType, brandedVariant, brandedSize, err });
    }
  }

  static isBaseThemeElement(variant, size) {
    const key = `${variant}.${size || "standard"}`;
    return this.baseVariants.includes(key);
  }

  // Inspector-only counterpart to `isBaseThemeElement`. Drives the
  // inspector's "treat this row as a standalone base" decision (no
  // inheriting badge, no override badge, no inheritance affordance)
  // without affecting render-time chain walks.
  static isInspectorBaseThemeElement(variant, size) {
    const key = `${variant}.${size || "standard"}`;
    return this.inspectorBaseVariants.includes(key);
  }

  static getDefaultValueForElementOption(optionKey, breakpoint, state, type, variant, size) {
    const config = useBrandingStore().defaultTemplateConfiguration;
    const typeConfig = config[type];
    const variantConfig = typeConfig[variant];

    let finalConfig = variantConfig;
    if (size) {
      finalConfig = variantConfig[size];
    }

    return finalConfig?.[`${state}.${optionKey}`];
  }

  /** ******************* Public methods *********************/
  isBrandedValueOverwritten(optionKey, breakpoint, state, type, variant, size) {
    const variantConfig = this.#getVariantConfig(
      type,
      `${variant}${size ? `.${size}` : ""}`,
      this.entireBrandingConfig,
    );

    breakpoint = !breakpoint || breakpoint === "default" ? "" : breakpoint;
    const key = `${state}${breakpoint ? `:${breakpoint}` : ""}.${optionKey}`;

    return !!variantConfig[key];
  }

  determineType() {
    throw new BrandingError(
      "static determineType() method should be implemented",
      {},
    );
  }

  determineVariant() {
    throw new BrandingError(
      "static determineVariant() method should be implemented",
      {},
    );
  }

  determineSize() {
    throw new BrandingError(
      "static determineSize() method should be implemented",
      {},
    );
  }

  findNextInheritanceLevel() {
    throw new BrandingError(
      "static findNextInheritanceLevel() method should be implemented",
      {},
    );
  }

  getBrandingConfigAsElements() {
    return mapBrandingConfigToElements(
      this.entireBrandingConfig,
      (type, variant, size) => this.#buildMergedConfig(type, variant, size),
    );
  }

  constructor() {
    this.entireBrandingConfig = useBrandingStore().configuration;
    this.defaultBrandingConfig = useBrandingStore().defaultTemplateConfiguration;
  }

  getRawValue(optionKey, breakpoint, state, type, variant, size, breakpoints, elementOptions = {}) {
    // `elementOptions` may arrive as either the legacy flat `state.key` /
    // `state:bp.key` shape (older callsites and unit fixtures) or the
    // persisted nested mode → bp → state → optionKey tree from
    // `DynamicElement.parsedOptions`. Normalise to flat up front so
    // downstream merging and the processInheritance walk both see the
    // same shape — without this, every element-instance override
    // silently dropped here on the way to the resolver.
    const flatElementOptions = this.constructor.flattenOptionsTreeIfNested(elementOptions);
    let relatedElementOptions = this.#getBrandedKeysFromElementOptions(flatElementOptions);
    relatedElementOptions = this.#parseAllStates(relatedElementOptions);
    const config = { ...this.#buildMergedConfig(type, variant, size), ...relatedElementOptions };

    return this.processInheritance(config, optionKey, breakpoint, state, breakpoints);
  }

  processInheritance(config, optionKey, breakpoint, state, breakpoints) {
    const flatConfig = this.constructor.flattenOptionsTreeIfNested(config);
    const hasBreakpoint = breakpoint && breakpoint !== "default";
    const keyInConfig = `${state}${!hasBreakpoint ? "" : `:${breakpoint}`}.${optionKey}`;
    const valueAtConfig = flatConfig?.[keyInConfig];
    if (valueAtConfig !== undefined) {
      return valueAtConfig;
    }

    if (hasBreakpoint) {
      const inheritedFromBreakpoints = this.#traverseBreakpoints(optionKey, breakpoint, state, flatConfig, breakpoints);
      if (inheritedFromBreakpoints !== undefined) {
        return inheritedFromBreakpoints;
      }
    }

    const inheritFromBaseState = flatConfig?.[`${state}.${optionKey}`];
    if (inheritFromBaseState !== undefined) {
      return inheritFromBaseState;
    }

    return flatConfig?.[`default.${optionKey}`];
  }

  // Flatten the persisted nested options tree
  // (`{<mode>: {<bp>: {<state>: {<optionKey>: {r, c, ...}}}}}`) into the
  // legacy flat shape (`{"<state>.<optionKey>": <value>}` and
  // `{"<state>:<bp>.<optionKey>": <value>}`) that processInheritance and
  // the branding-template merge rely on. Returns the input unchanged when
  // it isn't a recognised nested tree, so flat callers stay unaffected.
  //
  // Picks `r` (raw) over `c` (compiled) — processInheritance is consumed
  // by `determineType`/`determineVariant`/`determineSize`, all of which
  // want the raw enum value the user picked (e.g. "subheading"), not the
  // compiled CSS payload.
  static flattenOptionsTreeIfNested(tree) {
    if (!tree || typeof tree !== "object") {
      return tree ?? {};
    }
    const looksNested = "light-mode" in tree || "dark-mode" in tree;
    if (!looksNested) {
      return tree;
    }
    const modeData = tree["light-mode"] ?? tree["dark-mode"] ?? {};
    const flat = {};
    for (const breakpoint of Object.keys(modeData)) {
      const bpData = modeData[breakpoint];
      if (!bpData || typeof bpData !== "object") {
        continue;
      }
      for (const state of Object.keys(bpData)) {
        const stateData = bpData[state];
        if (!stateData || typeof stateData !== "object") {
          continue;
        }
        for (const optionKey of Object.keys(stateData)) {
          const leaf = stateData[optionKey];
          if (leaf === undefined || leaf === null) {
            continue;
          }
          const value = typeof leaf === "object" && !Array.isArray(leaf)
            ? leaf.r ?? leaf.c
            : leaf;
          if (value === undefined) {
            continue;
          }
          const flatKey = breakpoint === "default"
            ? `${state}.${optionKey}`
            : `${state}:${breakpoint}.${optionKey}`;
          flat[flatKey] = value;
        }
      }
    }
    return flat;
  }

  /** ******************* Private methods *********************/

  #getBrandedKeysFromElementOptions(elementOptions) {
    return Object.keys(elementOptions).reduce((relatedOptions, key) => {
      const relatedOptionKey = key.split(".").at(-1);
      if (this.constructor.isThemableOptionKey(relatedOptionKey)) {
        relatedOptions[key] = elementOptions[key];
      }
      return relatedOptions;
    }, {});
  }

  #parseAllStates(config) {
    return Object.keys(config).reduce((parsedStates, optionKey) => {
      if (optionKey.startsWith("default.")) {
        const [, optKey] = optionKey.split(".");
        // TODO: make this support other states and not only hover - hover is the only state currently supported
        if (!config[`hover.${optKey}`]) {
          parsedStates[`hover.${optKey}`] = config[optionKey];
        }
      }
      parsedStates[optionKey] = config[optionKey];
      return parsedStates;
    }, {});
  }

  #buildMergedConfig(type, variant, size) {
    const chain = this.#resolveChain(variant, size);

    return chain.reduce((merged, chainKey) => {
      let config = this.#getVariantConfig(type, chainKey, this.entireBrandingConfig);
      const defaultConfig = this.#getVariantConfig(type, chainKey, this.defaultBrandingConfig);

      config = this.#parseAllStates(config);

      return { ...merged, ...defaultConfig, ...config };
    }, {});
  }

  #resolveChain(variant, size) {
    const key = size ? `${variant}.${size}` : variant;
    const ancestors = this.constructor.inheritanceStructure.inherits?.[key] ?? [];
    return [...ancestors, key];
  }

  #getVariantConfig(type, chainKey, brandingConfig) {
    const [variant, size] = chainKey.split(".");
    const config = size
      ? brandingConfig?.[type]?.[variant]?.[size]
      : brandingConfig?.[type]?.[variant];
    return config ?? {};
  }

  #getAvailableBreakpoints(currentBreakpointAlias, allBreakpoints) {
    if (!currentBreakpointAlias || currentBreakpointAlias === "default") {
      return [];
    }
    const currentBreakpoint = allBreakpoints.find(bp => bp.alias === currentBreakpointAlias);
    // The active alias may not exist in the supplied breakpoints — e.g.
    // a breakpoint was removed (or hidden + filtered out by a caller)
    // while the editor's `activeBreakpointAlias` still pointed at it.
    // Bail to an empty walk in that case so a missing reference doesn't
    // throw `Cannot read properties of undefined (reading 'value')` deep
    // inside the inheritance chain.
    if (!currentBreakpoint?.value) {
      return [];
    }
    const currentValue = parseInt(currentBreakpoint.value.replace("px", ""));
    return allBreakpoints.map(bp => {
      return { ...bp, value: parseInt(bp.value.replace("px", "")) };
    }).filter(bp => {
      if (useConfigurationStore().designVariant !== "mobile") {
        return bp.value !== null && bp.value > currentValue;
      }
      return bp.value !== null && bp.value < currentValue;
    }).sort((a, b) => a.value - b.value); // Sort ascending
  }

  #traverseBreakpoints(optionKey, breakpoint, state, config, breakpoints) {
    const availableBreakpoints = this.#getAvailableBreakpoints(breakpoint, breakpoints);

    const atBreakpointState = config?.[`${state}:${breakpoint}.${optionKey}`];
    if (atBreakpointState !== undefined) {
      return atBreakpointState;
    }

    const atBreakpointDefault = config?.[`default:${breakpoint}.${optionKey}`];
    if (atBreakpointDefault !== undefined) {
      return atBreakpointDefault;
    }

    for (const { alias: bp } of availableBreakpoints) {
      const atBpState = config?.[`${state}:${bp}.${optionKey}`];
      if (atBpState !== undefined) {
        return atBpState;
      }

      const atBpDefault = config?.[`default:${bp}.${optionKey}`];
      if (atBpDefault !== undefined) {
        return atBpDefault;
      }
    }

    return undefined;
  }
}
